import { Separator } from '@sea-snag/ui';
import { createFileRoute, Link } from '@tanstack/react-router';

import { LandingFooter, LandingHeader } from '../components/landing';

export const Route = createFileRoute('/terms')({
  component: TermsPage,
});

function TermsPage() {
  return (
    <div className="min-h-screen bg-background">
      <LandingHeader />
      <main className="mx-auto max-w-3xl px-6 py-16">
        <h1 className="text-4xl font-bold tracking-tight">Terms of Service</h1>
        <p className="mt-2 text-sm text-muted-foreground">Last updated: February 16, 2026</p>

        <Separator className="my-8" />

        <div className="space-y-8 text-muted-foreground [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:text-foreground [&_h3]:mt-4 [&_h3]:text-base [&_h3]:font-medium [&_h3]:text-foreground [&_p]:mt-3 [&_p]:leading-relaxed [&_ul]:mt-3 [&_ul]:list-disc [&_ul]:space-y-1 [&_ul]:pl-6">
          <section>
            <h2>1. Acceptance of Terms</h2>
            <p>
              These Terms of Service (&quot;Terms&quot;) constitute a legally binding agreement
              between you (individually or on behalf of the organization you represent) and Sea Snag
              (&quot;we&quot;, &quot;our&quot;, or &quot;us&quot;) governing your access to and use
              of the Sea Snag platform at seasnag.com and all related services (collectively, the
              &quot;Service&quot;).
            </p>
            <p>
              By creating an account, accessing, or using the Service, you acknowledge that you have
              read, understood, and agree to be bound by these Terms and our{' '}
              <Link to="/privacy" className="text-foreground underline">
                Privacy Policy
              </Link>
              . If you are accepting these Terms on behalf of an organization, you represent and
              warrant that you have the authority to bind that organization to these Terms.
            </p>
            <p>If you do not agree to these Terms, you must not access or use the Service.</p>
          </section>

          <section>
            <h2>2. Description of Service</h2>
            <p>
              Sea Snag is a software-as-a-service (SaaS) platform designed for yacht charter
              agencies. The Service provides a suite of tools including:
            </p>
            <ul>
              <li>
                <strong>Fleet Management:</strong> Yacht listings, specifications, photo galleries,
                availability calendars, and maintenance tracking.
              </li>
              <li>
                <strong>Dynamic Pricing:</strong> Seasonal pricing rules, automated pricing
                adjustments, and pricing calendar management.
              </li>
              <li>
                <strong>Analytics &amp; Insights:</strong> Revenue dashboards, occupancy trends,
                fleet utilization metrics, and business performance reports.
              </li>
              <li>
                <strong>Team Collaboration:</strong> Multi-user organizations with role-based access
                control, permissions management, and internal messaging.
              </li>
              <li>
                <strong>Integrations:</strong> Payment processing via Stripe, file storage, email
                notifications, and Partner API for external system connectivity.
              </li>
            </ul>
            <p>
              Features may vary based on your subscription plan. We reserve the right to modify,
              add, or discontinue features with reasonable notice.
            </p>
          </section>

          <section>
            <h2>3. Account Registration &amp; Security</h2>

            <h3>3.1 Eligibility</h3>
            <p>
              To use the Service, you must be at least 18 years of age (or the age of legal majority
              in your jurisdiction) and have the legal capacity to enter into a binding agreement.
              The Service is intended for business use by yacht charter professionals and agencies.
            </p>

            <h3>3.2 Account Creation</h3>
            <p>When you create an account, you agree to:</p>
            <ul>
              <li>Provide accurate, current, and complete registration information.</li>
              <li>Keep your account information up to date.</li>
              <li>
                Maintain the confidentiality of your login credentials and not share them with
                others.
              </li>
              <li>
                Immediately notify us at{' '}
                <a href="mailto:security@seasnag.com" className="text-foreground underline">
                  security@seasnag.com
                </a>{' '}
                if you suspect unauthorized access to your account.
              </li>
            </ul>

            <h3>3.3 Account Responsibility</h3>
            <p>
              You are responsible for all activity that occurs under your account, whether or not
              you authorized it. Organization owners are responsible for managing team member
              access, assigning appropriate roles and permissions, and ensuring all members comply
              with these Terms.
            </p>
          </section>

          <section>
            <h2>4. Subscription Plans &amp; Payments</h2>

            <h3>4.1 Plans &amp; Pricing</h3>
            <p>
              The Service offers multiple subscription tiers with varying features and usage limits.
              Current pricing is available on our pricing page. All prices are listed in the
              currency displayed and are exclusive of applicable taxes unless otherwise stated.
            </p>

            <h3>4.2 Billing</h3>
            <ul>
              <li>
                Subscriptions are billed in advance on a monthly or annual basis depending on the
                plan you select.
              </li>
              <li>
                Payment is processed through Stripe. By subscribing, you authorize Stripe to charge
                your payment method on a recurring basis.
              </li>
              <li>
                Failed payments may result in service interruption. We will make reasonable attempts
                to notify you of payment issues before suspending access.
              </li>
            </ul>

            <h3>4.3 Free Trial</h3>
            <p>
              If we offer a free trial period, you may use the Service without charge during that
              period. At the end of the trial, you must subscribe to a paid plan to continue using
              the Service. We will not charge you without your express consent.
            </p>

            <h3>4.4 Price Changes</h3>
            <p>
              We may adjust subscription pricing from time to time. We will provide at least 30
              days&apos; advance notice of any price increase via email and in-platform
              notification. Price changes take effect at the start of your next billing cycle. If
              you do not agree with the new pricing, you may cancel your subscription before the
              change takes effect.
            </p>

            <h3>4.5 Refunds</h3>
            <p>
              Subscription fees are generally non-refundable. However, if you cancel within the
              first 14 days of your initial subscription, you may request a full refund. Refund
              requests should be sent to{' '}
              <a href="mailto:billing@seasnag.com" className="text-foreground underline">
                billing@seasnag.com
              </a>
              . We may also issue refunds at our discretion in cases of service outages or billing
              errors.
            </p>

            <h3>4.6 Cancellation</h3>
            <p>
              You may cancel your subscription at any time through your account settings or by
              contacting support. Upon cancellation, your subscription remains active until the end
              of the current billing period. After that, your account will be downgraded and you
              will lose access to paid features. Your data will be retained for 90 days after
              cancellation, during which you may resubscribe to restore access. After 90 days, your
              data may be permanently deleted.
            </p>
          </section>

          <section>
            <h2>5. Acceptable Use</h2>
            <p>
              You agree to use the Service only for lawful purposes and in accordance with these
              Terms. You specifically agree not to:
            </p>
            <ul>
              <li>
                Use the Service for any purpose other than managing legitimate yacht charter or
                marine tourism operations.
              </li>
              <li>
                Violate any applicable local, national, or international law or regulation,
                including maritime regulations, data protection laws, and anti-money laundering
                requirements.
              </li>
              <li>
                Upload, transmit, or store any content that is unlawful, harmful, threatening,
                defamatory, obscene, or otherwise objectionable.
              </li>
              <li>
                Impersonate any person or entity, or falsely state or misrepresent your affiliation
                with a person or entity.
              </li>
              <li>
                Attempt to gain unauthorized access to any part of the Service, other user accounts,
                or any systems or networks connected to the Service.
              </li>
              <li>Introduce viruses, trojans, worms, or other malicious code into the Service.</li>
              <li>
                Use automated scripts, bots, scrapers, or other automated means to access the
                Service without our prior written consent (authorized Partner API usage excepted).
              </li>
              <li>
                Interfere with, disrupt, or place an unreasonable burden on the Service or its
                infrastructure.
              </li>
              <li>
                Resell, redistribute, or sublicense access to the Service to third parties without
                our express written permission.
              </li>
              <li>
                Store personal data of individuals (guests, crew, etc.) without having obtained the
                necessary consents under applicable data protection laws.
              </li>
            </ul>
            <p>
              We reserve the right to investigate violations and may suspend or terminate accounts
              that breach these terms.
            </p>
          </section>

          <section>
            <h2>6. Your Data &amp; Content</h2>

            <h3>6.1 Ownership</h3>
            <p>
              You retain full ownership of all data, content, and materials that you upload, enter,
              or transmit through the Service (&quot;Your Data&quot;). We do not claim any ownership
              rights over Your Data.
            </p>

            <h3>6.2 License to Us</h3>
            <p>
              By using the Service, you grant us a limited, non-exclusive, worldwide license to
              host, store, process, and display Your Data solely for the purpose of providing,
              maintaining, and improving the Service. This license terminates when you delete Your
              Data or close your account.
            </p>

            <h3>6.3 Your Responsibilities</h3>
            <ul>
              <li>
                You are solely responsible for the accuracy, quality, and legality of Your Data and
                the means by which you acquired it.
              </li>
              <li>
                You must ensure you have all necessary rights, licenses, and consents to upload
                customer and guest data, yacht photos, and any other content.
              </li>
              <li>
                You are responsible for complying with all applicable data protection laws
                (including GDPR) with respect to the personal data of your customers and guests that
                you store on the platform.
              </li>
            </ul>

            <h3>6.4 Data Export</h3>
            <p>
              You may export Your Data at any time through the platform&apos;s export features or by
              requesting a data export from our support team. We will provide your data in a
              standard, machine-readable format (CSV or JSON).
            </p>

            <h3>6.5 Backups</h3>
            <p>
              While we maintain regular backups of our systems, you are responsible for maintaining
              your own backups of Your Data. We recommend regularly exporting critical business
              data. We are not liable for any loss of Your Data except where caused by our gross
              negligence or willful misconduct.
            </p>
          </section>

          <section>
            <h2>7. Intellectual Property</h2>

            <h3>7.1 Our IP</h3>
            <p>
              The Service, including all software, code, design, text, graphics, logos, icons,
              images, and the overall look and feel, is the exclusive property of Sea Snag and is
              protected by copyright, trademark, and other intellectual property laws. All rights
              not expressly granted to you are reserved by Sea Snag.
            </p>

            <h3>7.2 Restrictions</h3>
            <p>You may not:</p>
            <ul>
              <li>Copy, modify, or create derivative works based on the Service.</li>
              <li>
                Reverse-engineer, decompile, disassemble, or otherwise attempt to discover the
                source code of the Service.
              </li>
              <li>
                Remove, alter, or obscure any copyright, trademark, or other proprietary notices.
              </li>
              <li>Use our trademarks, logos, or brand names without our prior written consent.</li>
            </ul>

            <h3>7.3 Feedback</h3>
            <p>
              If you provide us with suggestions, ideas, or feedback about the Service, you grant us
              an unrestricted, perpetual, irrevocable, royalty-free license to use, modify, and
              incorporate such feedback without any obligation to you.
            </p>
          </section>

          <section>
            <h2>8. Third-Party Integrations</h2>
            <p>
              The Service integrates with third-party services to provide core functionality. Your
              use of these integrations is subject to their respective terms:
            </p>
            <ul>
              <li>
                <strong>Stripe:</strong> Payment processing, subscription management, and billing
                are handled by Stripe. Your use of payment features is subject to Stripe&apos;s
                terms of service and privacy policy.
              </li>
              <li>
                <strong>DigitalOcean Spaces:</strong> File uploads (yacht photos, documents) may be
                stored using DigitalOcean&apos;s object storage service.
              </li>
              <li>
                <strong>Email Services:</strong> Transactional emails (pricing notifications, team
                invitations) are delivered through third-party email providers.
              </li>
            </ul>
            <p>
              We are not responsible for the performance, availability, or practices of third-party
              services. We will make reasonable efforts to notify you of any changes to third-party
              integrations that materially affect the Service.
            </p>
          </section>

          <section>
            <h2>9. Partner API</h2>
            <p>
              If your subscription includes access to the Sea Snag Partner API, the following
              additional terms apply:
            </p>
            <ul>
              <li>
                API access is provided for the purpose of integrating your charter operations with
                authorized external platforms and services.
              </li>
              <li>
                You must not exceed the rate limits specified in the API documentation for your
                subscription tier.
              </li>
              <li>
                API credentials are confidential and must not be shared or embedded in client-side
                code.
              </li>
              <li>
                We reserve the right to revoke API access if we detect misuse, excessive load, or
                security concerns.
              </li>
              <li>
                We may update the API and its endpoints. We will provide reasonable notice for
                breaking changes and maintain backward compatibility where feasible.
              </li>
            </ul>
          </section>

          <section>
            <h2>10. Service Availability &amp; Support</h2>

            <h3>10.1 Uptime</h3>
            <p>
              We strive to maintain high availability of the Service but do not guarantee
              uninterrupted or error-free access. Scheduled maintenance will be performed during
              low-usage periods when possible, and we will provide advance notice for planned
              downtime.
            </p>

            <h3>10.2 Support</h3>
            <p>
              Support is available via email at{' '}
              <a href="mailto:support@seasnag.com" className="text-foreground underline">
                support@seasnag.com
              </a>
              . Response times and support channels may vary based on your subscription plan. We aim
              to respond to all support requests within 2 business days.
            </p>

            <h3>10.3 Updates</h3>
            <p>
              We regularly update the Service with bug fixes, improvements, and new features.
              Updates are applied automatically and may occasionally require brief periods of
              downtime. We are not obligated to provide any specific feature or update.
            </p>
          </section>

          <section>
            <h2>11. Disclaimer of Warranties</h2>
            <p>
              THE SERVICE IS PROVIDED ON AN &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; BASIS
              WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE. TO
              THE MAXIMUM EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, INCLUDING BUT NOT
              LIMITED TO:
            </p>
            <ul>
              <li>IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE.</li>
              <li>WARRANTIES OF NON-INFRINGEMENT.</li>
              <li>
                WARRANTIES THAT THE SERVICE WILL BE UNINTERRUPTED, TIMELY, SECURE, OR ERROR-FREE.
              </li>
              <li>
                WARRANTIES REGARDING THE ACCURACY OR RELIABILITY OF ANY INFORMATION OBTAINED THROUGH
                THE SERVICE, INCLUDING PRICING RECOMMENDATIONS AND ANALYTICS DATA.
              </li>
            </ul>
            <p>
              You acknowledge that you use the Service at your own risk and that business decisions
              based on analytics, pricing recommendations, or other platform outputs are solely your
              responsibility.
            </p>
          </section>

          <section>
            <h2>12. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL SEA SNAG, ITS
              AFFILIATES, DIRECTORS, OFFICERS, EMPLOYEES, AGENTS, OR LICENSORS BE LIABLE FOR:
            </p>
            <ul>
              <li>
                ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT
                NOT LIMITED TO LOSS OF PROFITS, REVENUE, DATA, BUSINESS OPPORTUNITIES, OR GOODWILL.
              </li>
              <li>
                ANY LOSS ARISING FROM YOUR RELIANCE ON ANALYTICS, PRICING RECOMMENDATIONS, OR OTHER
                PLATFORM OUTPUTS.
              </li>
              <li>ANY DAMAGES RESULTING FROM UNAUTHORIZED ACCESS TO OR ALTERATION OF YOUR DATA.</li>
              <li>
                ANY DAMAGES ARISING FROM THE CONDUCT OF THIRD PARTIES, INCLUDING OTHER USERS OR
                SERVICE PROVIDERS.
              </li>
            </ul>
            <p>
              OUR TOTAL AGGREGATE LIABILITY FOR ALL CLAIMS ARISING UNDER OR IN CONNECTION WITH THESE
              TERMS SHALL NOT EXCEED THE TOTAL AMOUNT YOU HAVE PAID TO US IN THE TWELVE (12) MONTHS
              IMMEDIATELY PRECEDING THE EVENT GIVING RISE TO THE CLAIM.
            </p>
            <p>
              SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OR LIMITATION OF CERTAIN DAMAGES. IN
              SUCH JURISDICTIONS, OUR LIABILITY SHALL BE LIMITED TO THE MAXIMUM EXTENT PERMITTED BY
              LAW.
            </p>
          </section>

          <section>
            <h2>13. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless Sea Snag and its affiliates,
              officers, directors, employees, and agents from and against any claims, liabilities,
              damages, losses, costs, and expenses (including reasonable legal fees) arising from:
            </p>
            <ul>
              <li>Your use of the Service or violation of these Terms.</li>
              <li>Your Data or any content you upload to the platform.</li>
              <li>
                Your violation of any applicable law or regulation, including data protection laws.
              </li>
              <li>
                Your infringement of any third-party rights, including intellectual property or
                privacy rights.
              </li>
              <li>
                Any claim by a third party (including your customers or guests) related to data you
                stored on the platform.
              </li>
            </ul>
          </section>

          <section>
            <h2>14. Termination</h2>

            <h3>14.1 By You</h3>
            <p>
              You may terminate your account at any time by canceling your subscription through
              account settings or by contacting{' '}
              <a href="mailto:support@seasnag.com" className="text-foreground underline">
                support@seasnag.com
              </a>
              . Cancellation takes effect at the end of your current billing period.
            </p>

            <h3>14.2 By Us</h3>
            <p>We may suspend or terminate your access to the Service:</p>
            <ul>
              <li>Immediately, if you materially breach these Terms.</li>
              <li>Immediately, if required by law or to protect other users.</li>
              <li>
                With 30 days&apos; notice, if we discontinue the Service or for any other reason.
              </li>
            </ul>

            <h3>14.3 Effect of Termination</h3>
            <p>Upon termination:</p>
            <ul>
              <li>Your right to access and use the Service ceases immediately.</li>
              <li>You may request an export of Your Data within 30 days of termination.</li>
              <li>
                We may delete Your Data after 90 days, unless we are required to retain it by law.
              </li>
              <li>
                Sections of these Terms that by their nature should survive termination (including
                intellectual property, limitation of liability, indemnification, and dispute
                resolution) shall continue to apply.
              </li>
            </ul>
          </section>

          <section>
            <h2>15. Changes to These Terms</h2>
            <p>We may modify these Terms at any time. When we make material changes, we will:</p>
            <ul>
              <li>Update the &quot;Last updated&quot; date at the top of this page.</li>
              <li>
                Notify you via email and/or in-platform notification at least 30 days before the
                changes take effect.
              </li>
              <li>Provide a summary of key changes.</li>
            </ul>
            <p>
              Your continued use of the Service after the effective date constitutes acceptance of
              the updated Terms. If you do not agree with the changes, you must stop using the
              Service and cancel your subscription before the changes take effect.
            </p>
          </section>

          <section>
            <h2>16. Dispute Resolution</h2>

            <h3>16.1 Informal Resolution</h3>
            <p>
              Before initiating formal proceedings, you agree to contact us at{' '}
              <a href="mailto:legal@seasnag.com" className="text-foreground underline">
                legal@seasnag.com
              </a>{' '}
              and attempt to resolve any dispute informally for at least 30 days.
            </p>

            <h3>16.2 Governing Law</h3>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the
              jurisdiction in which Sea Snag is incorporated, without regard to its conflict of law
              provisions.
            </p>

            <h3>16.3 Jurisdiction</h3>
            <p>
              Any legal proceedings arising from these Terms shall be brought exclusively in the
              courts of the jurisdiction in which Sea Snag is incorporated, and you consent to the
              personal jurisdiction of such courts.
            </p>
          </section>

          <section>
            <h2>17. General Provisions</h2>
            <ul>
              <li>
                <strong>Entire Agreement:</strong> These Terms, together with our Privacy Policy,
                constitute the entire agreement between you and Sea Snag regarding the Service.
              </li>
              <li>
                <strong>Severability:</strong> If any provision of these Terms is found to be
                unenforceable, the remaining provisions shall continue in full force and effect.
              </li>
              <li>
                <strong>Waiver:</strong> Our failure to enforce any provision of these Terms does
                not constitute a waiver of that provision or any other provision.
              </li>
              <li>
                <strong>Assignment:</strong> You may not assign or transfer your rights under these
                Terms without our prior written consent. We may assign our rights and obligations
                without restriction.
              </li>
              <li>
                <strong>Force Majeure:</strong> We are not liable for any failure or delay in
                performance resulting from circumstances beyond our reasonable control, including
                natural disasters, acts of government, internet disruptions, or third-party service
                failures.
              </li>
            </ul>
          </section>

          <section>
            <h2>18. Contact Us</h2>
            <p>If you have questions about these Terms of Service, please contact us:</p>
            <ul className="!list-none !pl-0">
              <li>
                Legal inquiries:{' '}
                <a href="mailto:legal@seasnag.com" className="text-foreground underline">
                  legal@seasnag.com
                </a>
              </li>
              <li>
                General support:{' '}
                <a href="mailto:support@seasnag.com" className="text-foreground underline">
                  support@seasnag.com
                </a>
              </li>
              <li>
                Billing:{' '}
                <a href="mailto:billing@seasnag.com" className="text-foreground underline">
                  billing@seasnag.com
                </a>
              </li>
            </ul>
          </section>
        </div>
      </main>
      <LandingFooter />
    </div>
  );
}
